import { Document, type DocumentData, IndexedDB } from "flexsearch"
import { z } from "zod"

export const SearchDocumentSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  body: z.string().nullable(),
})
export type SearchDocument = z.infer<typeof SearchDocumentSchema>

export type SearchResult = {
  order: number
  highlights: Record<keyof SearchDocument, string> | undefined
}

const BATCH_SIZE = 100
const BATCH_DELAY = 10

export class SearchIndex {
  private db: IndexedDB
  private documentIndex: Document<DocumentData, false, IndexedDB>

  constructor(indexName: string) {
    this.db = new IndexedDB(indexName)
    this.documentIndex = new Document({
      db: this.db,
      tokenize: "reverse",
      resolution: 9,
      document: {
        id: "id",
        store: true,
        index: [
          { field: "title", score: () => 4 },
          { field: "description", score: () => 2 },
          { field: "body", score: () => 1 },
        ],
      },
    })
  }

  async waitUntilReady() {
    // https://github.com/nextapps-de/flexsearch/pull/516
    await (this.documentIndex as any).db
  }

  async addDocuments(
    ids: string[],
    getDocuments: (ids: string[]) => Promise<SearchDocument[]>,
  ) {
    await this.waitUntilReady()

    const currentIds = new Set(await this.getCurrentIndexedIds())
    const newIds = new Set(ids)
    const idsToAdd = Array.from(newIds.difference(currentIds))

    if (idsToAdd.length > 0) {
      await this.processUpsertBatches(idsToAdd, getDocuments, "add")
    }
  }

  async updateDocuments(
    ids: string[],
    getDocuments: (ids: string[]) => Promise<SearchDocument[]>,
  ) {
    await this.waitUntilReady()

    const currentIds = new Set(await this.getCurrentIndexedIds())
    const newIds = new Set(ids)
    const idsToUpdate = Array.from(newIds.intersection(currentIds))

    if (idsToUpdate.length > 0) {
      await this.processUpsertBatches(idsToUpdate, getDocuments, "update")
    }
  }

  private async processUpsertBatches(
    ids: string[],
    getDocuments: (ids: string[]) => Promise<SearchDocument[]>,
    operation: "add" | "update",
  ) {
    const batches = this.createBatches(ids)

    for (const [i, batch] of batches.entries()) {
      try {
        const documents = await getDocuments(batch)

        for (const document of documents) {
          this.documentIndex[operation](document)
        }

        await this.documentIndex.commit()

        if (i < batches.length - 1) {
          await this.delay()
        }
      } catch (error) {
        console.error(`Error processing ${operation} batch ${i + 1}:`, error)
      }
    }
  }

  private createBatches(ids: string[]): string[][] {
    const batches: string[][] = []
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE))
    }
    return batches
  }

  private delay(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, BATCH_DELAY))
  }

  async deleteDocuments(ids: string[]) {
    await this.waitUntilReady()

    if (ids.length === 0) {
      return
    }

    await this.processDeletionBatches(ids)
  }

  private async processDeletionBatches(ids: string[]) {
    const batches = this.createBatches(ids)

    for (const [i, batch] of batches.entries()) {
      try {
        for (const id of batch) {
          this.documentIndex.remove(id)
        }

        await this.documentIndex.commit()

        if (i < batches.length - 1) {
          await this.delay()
        }
      } catch (error) {
        console.error(`Error processing deletion batch ${i + 1}:`, error)
      }
    }
  }

  private async getCurrentIndexedIds(): Promise<string[]> {
    // We are using private properties here!!
    const cheatIndex = this.documentIndex as any
    const db = cheatIndex.index.get(cheatIndex.field[0]).db.db
    if (!db) throw new Error("No database found")

    return new Promise((resolve, reject) => {
      const transaction = db.transaction("reg", "readonly")
      const objectStore = transaction.objectStore("reg")
      const request = objectStore.getAllKeys()

      request.onsuccess = request.oncomplete = function onSuccess() {
        resolve(request.result ?? [])
      }

      request.onerror = request.onblocked = function onError() {
        console.error("Error getting indexed IDs:", request.error)
        reject(request.error)
      }
    })
  }

  async search(query: string, limit = 50) {
    await this.waitUntilReady()

    return await this.documentIndex.searchAsync(query, {
      limit,
      merge: true,
      suggest: true,
      cache: true,
      highlight: {
        template: "{{$1}}",
        merge: true,
        clip: false,
        boundary: {
          before: 20,
          after: 20,
          total: 100,
        },
      },
    })
  }
}

export const publisherIndex = new SearchIndex("publisher")
export const postsIndex = new SearchIndex("posts")
export const storyIndex = new SearchIndex("story")

// TODO: explore to merge documents and use tags to filter by type?
// TODO: use tags?
